// 版面斷點（批171）。
//
// ★★ 副本：正本在 `iplusone-cs/frontend/src/lib/breakpoints.ts`，改一邊要改另一邊。
//    LS 後台、portal、CS 工作台三個前端各自建置、不共用套件，所以各放一份；
//    `breakpoints.spec.ts` 逐字比對正本，漏改一邊就紅。
//
// - CSS：`vite.config.ts` 的 `ipoBreakpoints` PostCSS 外掛把 `@media (--mobile)` 這類寫法換成下面的查詢字串
//   （語法同 CSS 規格草案的 custom media，等瀏覽器原生支援時可以直接沿用）。
// - JS：`lib/viewport.ts` 用同一組字串做 matchMedia（`isMobile`／`isCompact`）。
//
// ★ 本檔只放純字串常數、不 import 任何東西——`vite.config.ts` 在 Node 端 import 它。
//
// 三層版面（LS 後台）：
//
// | 版面 | 條件 | 長相 |
// |---|---|---|
// | 手機 mobile | ≤768px，或「螢幕短邊 ≤500 的觸控裝置」橫放（≤960px 寬） | 頂欄＋漢堡抽屜、表單單欄、對話框全螢幕 |
// | 平板 tablet | 769～1199.98px（不是手機） | 頂欄＋漢堡抽屜（側欄讓出 240px 給內容）、表格可橫捲 |
// | 桌機 desktop | ≥1200px | 原本的桌機版面（批171 起刻意不動） |
//
// 手機橫放為什麼看「螢幕」不看視窗高度：Android 加上 `interactive-widget=resizes-content`
// （CS 工作台的 index.html）後鍵盤一開就把視窗高度縮到 500 以下，用視窗高度判斷的話，小平板橫放打字時
// 版面會在手機／平板之間來回翻（翻版會重掛輸入框→鍵盤收起→高度恢復→再翻回去）。
// `device-width／height` 是螢幕尺寸，不受鍵盤影響；兩個都要寫——iOS 的 screen 不隨方向旋轉、Android 會。
// 寬度上限 960：iPhone Pro Max 橫放 956 仍算手機；手機開「電腦版網站」（版面 980 寬）不算。

/** 手機觸控橫放的共同條件（不含寬度）。 */
const TOUCH_SHORT_SCREEN = '(hover: none) and (pointer: coarse)'

/**
 * 手機橫放是否算手機版面（主人 10-11 裁示：算）。批171 分兩步上：第 1 步純重構時是 false
 * （全部尺寸逐像素不變），第 2 步打開。保留這個開關是為了萬一實機出問題能一行退回。
 */
export const LANDSCAPE_PHONE_IS_MOBILE = true

const MOBILE_FULL = [
  '(max-width: 768px)',
  `(max-width: 960px) and (max-device-width: 500px) and ${TOUCH_SHORT_SCREEN}`,
  `(max-width: 960px) and (max-device-height: 500px) and ${TOUCH_SHORT_SCREEN}`,
].join(', ')

/**
 * MOBILE_FULL 的補集（迪摩根展開）：custom media 不能接 `not`，需要「非手機」時用這個。
 * mobile ＝ A ∨ (B ∧ T ∧ (D ∨ E))，A＝寬≤768、B＝寬≤960、T＝hover:none∧pointer:coarse、D／E＝螢幕寬／高≤500；
 * 補集 ＝ ¬A ∧ (¬B ∨ ¬T ∨ (¬D ∧ ¬E))，¬T 展開成 hover:hover、pointer:fine、pointer:none 三種。
 * 768.02／960.02／500.02：整數斷點配對會在 DPR 非整數時出現死區（見記憶 gotcha-media-query-integer-breakpoint-gap）。
 */
const NOT_MOBILE_FULL = [
  '(min-width: 960.02px)',
  '(min-width: 768.02px) and (min-device-width: 500.02px) and (min-device-height: 500.02px)',
  '(min-width: 768.02px) and (hover: hover)',
  '(min-width: 768.02px) and (pointer: fine)',
  '(min-width: 768.02px) and (pointer: none)',
].join(', ')

/** CSS 用的 custom media 名稱 → 查詢字串。名稱要跟 CSS 裡 `@media (--name)` 的 name 一致。 */
export const MEDIA = {
  /** 手機版面（直向 ≤768，或手機橫放）。 */
  mobile: LANDSCAPE_PHONE_IS_MOBILE ? MOBILE_FULL : '(max-width: 768px)',
  /** `mobile` 的補集。 */
  notMobile: LANDSCAPE_PHONE_IS_MOBILE ? NOT_MOBILE_FULL : '(min-width: 769px)',
  /** 手機橫放（底部導覽列變矮、聚焦時讓位）。 */
  mobileLandscape: [
    `(max-width: 960px) and (orientation: landscape) and (max-device-width: 500px) and ${TOUCH_SHORT_SCREEN}`,
    `(max-width: 960px) and (orientation: landscape) and (max-device-height: 500px) and ${TOUCH_SHORT_SCREEN}`,
  ].join(', '),
  /** 手機＋平板（<1200）：平板跟手機共用的規則寫這個。 */
  compact: '(max-width: 1199.98px)',
} as const

export type MediaName = keyof typeof MEDIA

/** CSS 裡寫的名稱（`--not-mobile`）與 MEDIA 鍵（`notMobile`）的對照。 */
export const CUSTOM_MEDIA: Record<string, string> = {
  '--mobile': MEDIA.mobile,
  '--not-mobile': MEDIA.notMobile,
  '--mobile-landscape': MEDIA.mobileLandscape,
  '--compact': MEDIA.compact,
}

/** 平板與桌機的分界（px）。 */
export const DESKTOP_MIN_WIDTH = 1200

/**
 * 把 `@media` 的參數裡的 `(--name)` 換成查詢字串（`vite.config.ts` 的 PostCSS 外掛呼叫）。
 *
 * ★ custom media 只能**單獨**出現（可以用逗號跟別的查詢並列，不可以接 `and`／`not`／`only`）：
 *   `(--mobile) and (orientation: landscape)` 展開後會變成「A, B, C and (…)」，`and` 只綁到最後一段，
 *   語意整個錯掉而且瀏覽器不會報錯。需要組合時另外定義一個名稱（例如 `--mobile-landscape`）。
 *   `styles/breakpoints.guard.spec.ts` 掃全部樣式守住這條。
 */
export function expandCustomMedia(params: string): string {
  if (!params.includes('(--')) return params
  // 先拿掉 custom media 名稱再找關鍵字（`--not-mobile` 名稱裡的 not 不算）
  if (/\b(and|not|only)\b/.test(params.replace(/\(--[a-z-]+\)/g, ''))) {
    throw new Error(`custom media 不可與 and／not／only 組合：@media ${params}`)
  }
  return params.replace(/\((--[a-z-]+)\)/g, (_m, name: string) => {
    const q = CUSTOM_MEDIA[name]
    if (!q) throw new Error(`未定義的斷點 ${name}（定義在 src/lib/breakpoints.ts 的 CUSTOM_MEDIA）`)
    return q
  })
}
